import identity from '@/api/services/identity';
import { usePermissionsStore } from '@/store/usePermissionsStore';
import { usePortfolioStore } from '@/store/usePortfolioStore';
import { useAuth } from '@/composables/useAuth';

// Singleton state (shared across all components)
const isImpersonating = ref(false);
const impersonatorUsername = ref(null);
const impersonatedUsername = ref(null);
const isLoading = ref(false);
const error = ref(null);

export const useImpersonation = () => {
  const { checkAuth } = useAuth();
  const router = useRouter();

  /**
   * Start impersonating a user
   * @param {number} userId - User ID to impersonate
   * @param {string} username - Username for display
   */
  const startImpersonation = async (userId, username) => {
    isLoading.value = true;
    error.value = null;

    try {
      // Store original tokens in sessionStorage as backup
      const originalToken = localStorage.getItem('token');
      const originalRefreshToken = localStorage.getItem('refreshToken');
      sessionStorage.setItem('originalToken', originalToken);
      sessionStorage.setItem('originalRefreshToken', originalRefreshToken);

      // Call impersonate API
      const response = await identity.impersonate(userId);

      if (response?.accessToken) {
        // Swap tokens
        localStorage.setItem('token', response.accessToken);
        localStorage.setItem('refreshToken', response.refreshToken);

        // Update state
        isImpersonating.value = true;
        impersonatedUsername.value = username;
        sessionStorage.setItem('impersonatedUsername', username);

        // Reload user context and permissions
        await checkAuth();
        const permissionsStore = usePermissionsStore();
        await permissionsStore.loadPermissions(true); // Force reload

        // Reset and reload portfolio store for the impersonated user
        const portfolioStore = usePortfolioStore();
        portfolioStore.clearState();
        portfolioStore.setAdminStatus(permissionsStore.hasPermission('admin:read'));
        await portfolioStore.initialize(true); // Force reload with new token

        // Land where the impersonated user would land
        router.push('/home');

        return { success: true };
      }

      return { success: false, error: 'No access token received' };
    } catch (err) {
      const status = err?.status || err?.response?.status;

      if (status === 403) {
        error.value = 'You do not have permission to impersonate users';
      } else if (status === 400) {
        error.value = err?.data?.detail || 'Cannot impersonate this user';
      } else if (status === 404) {
        error.value = 'User not found';
      } else {
        error.value = err?.data?.detail || err?.message || 'Failed to start impersonation';
      }

      // Restore original tokens on failure
      const originalToken = sessionStorage.getItem('originalToken');
      const originalRefreshToken = sessionStorage.getItem('originalRefreshToken');
      if (originalToken) {
        localStorage.setItem('token', originalToken);
        localStorage.setItem('refreshToken', originalRefreshToken);
      }
      sessionStorage.removeItem('originalToken');
      sessionStorage.removeItem('originalRefreshToken');
      sessionStorage.removeItem('impersonatedUsername');

      return { success: false, error: error.value };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Stop impersonating and return to original identity
   */
  const stopImpersonation = async () => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await identity.stopImpersonate();

      if (response?.accessToken) {
        // Swap back to original tokens
        localStorage.setItem('token', response.accessToken);
        localStorage.setItem('refreshToken', response.refreshToken);

        // Clear session storage backup
        sessionStorage.removeItem('originalToken');
        sessionStorage.removeItem('originalRefreshToken');
        sessionStorage.removeItem('impersonatedUsername');

        // Reset state
        isImpersonating.value = false;
        impersonatorUsername.value = null;
        impersonatedUsername.value = null;

        // Reload user context and permissions
        await checkAuth();
        const permissionsStore = usePermissionsStore();
        await permissionsStore.loadPermissions(true);

        // Reset and reload portfolio store for the original user
        const portfolioStore = usePortfolioStore();
        portfolioStore.clearState();
        portfolioStore.setAdminStatus(permissionsStore.hasPermission('admin:read'));
        await portfolioStore.initialize(true); // Force reload with original token

        // Navigate to admin users page
        router.push('/admin/users');

        return { success: true };
      }

      return { success: false, error: 'No access token received' };
    } catch (err) {
      error.value = err?.data?.detail || err?.message || 'Failed to stop impersonation';
      return { success: false, error: error.value };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Check impersonation status on page load
   */
  const checkImpersonationStatus = async () => {
    try {
      const status = await identity.getImpersonationStatus();
      isImpersonating.value = status.isImpersonating;
      impersonatorUsername.value = status.impersonatorUsername;
      // Use API value, fall back to sessionStorage (API may not return this)
      impersonatedUsername.value = status.impersonatedUsername
        || sessionStorage.getItem('impersonatedUsername');
    } catch (err) {
      // Silent fail - check sessionStorage for impersonation state
      const storedUsername = sessionStorage.getItem('impersonatedUsername');
      if (storedUsername) {
        isImpersonating.value = true;
        impersonatedUsername.value = storedUsername;
      }
      console.warn('Failed to check impersonation status:', err);
    }
  };

  return {
    // State (readonly to prevent external mutation)
    isImpersonating: readonly(isImpersonating),
    impersonatorUsername: readonly(impersonatorUsername),
    impersonatedUsername: readonly(impersonatedUsername),
    isLoading: readonly(isLoading),
    error: readonly(error),
    // Actions
    startImpersonation,
    stopImpersonation,
    checkImpersonationStatus,
  };
};
